/* oxlint-disable no-console */
import { createLogger } from "@/utils/logger";

import type { Command, CommandProps, Editor, RawCommands } from "@tiptap/core";

const logger = createLogger("Tiptap:commands");

const MAX_ARGS_LENGTH = 160;
const TRACE_FRAMES = 10;

type UntypedRawCommand = (...args: unknown[]) => Command;

type EditorInternals = { commandManager: { rawCommands: RawCommands } };

const installed = new WeakSet<Editor>();

let depth = 0;

/**
 * Logs every tiptap command invocation at DEBUG level, nested calls indented.
 */
export function installTiptapCommandLogger(editor: Editor): void {
  if (!logger.enabled("DEBUG") || installed.has(editor)) {
    return;
  }

  installed.add(editor);

  const internals = editor as unknown as EditorInternals;

  internals.commandManager.rawCommands = Object.fromEntries(
    Object.entries(internals.commandManager.rawCommands).map(([name, command]) => [
      name,
      wrapCommand(name, command as UntypedRawCommand),
    ]),
  ) as RawCommands;

  logger.debug(`logging ${Object.keys(internals.commandManager.rawCommands).length} commands`);
}

function wrapCommand(name: string, command: UntypedRawCommand): UntypedRawCommand {
  return (...args: unknown[]) =>
    (props: CommandProps) => {
      // `can()` builds the transaction without a dispatcher, so nothing reaches the view.
      const mode = props.dispatch ? "run" : "can";
      const stack = depth === 0 ? callerStack() : "";

      console.groupCollapsed(`[Tiptap:commands] ${mode} ${name}(${formatArgs(args)})`);
      if (stack) console.log(stack);

      const stepsBefore = props.tr.steps.length;
      const selectionBefore = props.tr.selection;

      depth += 1;
      try {
        const result = command(...args)(props);
        console.log(`← ${name} = ${result}${formatEffects(props, stepsBefore, selectionBefore)}`);
        return result;
      } catch (error) {
        console.error(`✖ ${name} threw`, error);
        throw error;
      } finally {
        depth -= 1;
        console.groupEnd();
      }
    };
}

function formatEffects(
  props: CommandProps,
  stepsBefore: number,
  selectionBefore: CommandProps["tr"]["selection"],
): string {
  const effects: string[] = [];

  const addedSteps = props.tr.steps.length - stepsBefore;
  if (addedSteps > 0) {
    effects.push(`+${addedSteps} step${addedSteps > 1 ? "s" : ""}`);
  }
  if (props.tr.selection !== selectionBefore) {
    effects.push(`selection ${props.tr.selection.from}..${props.tr.selection.to}`);
  }

  return effects.length > 0 ? ` (${effects.join(", ")})` : "";
}

function formatArgs(args: unknown[]): string {
  const formatted = args.map(formatValue).join(", ");

  return formatted.length > MAX_ARGS_LENGTH
    ? `${formatted.slice(0, MAX_ARGS_LENGTH - 1)}…`
    : formatted;
}

function formatValue(value: unknown): string {
  if (typeof value === "string") {
    return JSON.stringify(value);
  }
  if (typeof value === "function") {
    return `${value.name || "anonymous"}()`;
  }
  if (value === null || typeof value !== "object") {
    return String(value);
  }

  // ProseMirror nodes and slices stringify into unreadable walls of JSON.
  const node = value as { type?: { name?: string }; nodeSize?: number };
  if (typeof node.nodeSize === "number" && node.type?.name) {
    return `<${node.type.name}>`;
  }

  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return `[${value.constructor?.name ?? "object"}]`;
  }
}

function callerStack(): string {
  const frames = new Error().stack?.split("\n").slice(1) ?? [];

  return frames
    .filter((frame) => !frame.includes("tiptapCommandLogger"))
    .slice(0, TRACE_FRAMES)
    .map((frame) => `      ${frame.trim()}`)
    .join("\n");
}
